<template>
  <b-card>
    <div v-if="loadingPage" class="loading">
      <Loading />
    </div>
    <div v-if="!loadingPage" class="custom-search">
      <header>
        <b-button-group
          v-if="$can('see', 'customPoll.chooseOwner')"
          class="mt-4 mb-4"
        >
          <b-button
            variant="outline-primary"
            :class="{ active: selectedOwner === owner.my }"
            @click="selectedOwner = owner.my"
            >{{ label.ownerPolls.myPolls }}</b-button
          >

          <b-button
            variant="outline-primary"
            :class="{ active: selectedOwner === owner.others }"
            @click="selectedOwner = owner.others"
            >{{ label.ownerPolls.otherPolls }}</b-button
          >
        </b-button-group>

        <b-nav class="link">
          <b-nav-item
            v-for="key in Object.keys(availableTabs)"
            :key="key"
            :active="currentTab === key"
            @click="$emit('changeStatus', key)"
            >{{ availableTabs[key] }}</b-nav-item
          >
        </b-nav>
        <div class="d-flex text-gray03 mt-3">
          <h6>{{ label.statusDescription[currentTab] }}</h6>
        </div>
      </header>

      <section class="mt-4">
        <div v-if="loading" class="poll-loading">
          <Loading />
          <p>{{ label.loadingPolls }}</p>
        </div>
        <b-card v-if="!loading">
          <div v-if="!polls.length" class="empty-labels">
            <img :src="'/home/empty-state-custom-survey.svg'" />
            <p>{{ emptyLabel.title }}</p>
            <small>{{ emptyLabel.description }}</small>
          </div>

          <b-table
            v-if="polls.length"
            responsive
            hover
            :items="items"
            :fields="tableFields"
            class="polls-table"
            :tbody-tr-class="currentTab !== status.done ? 'clickable' : ''"
            @row-clicked="rowClick"
          >
            <template
              v-if="optionsControll.canSee"
              v-slot:cell(settings)="data"
            >
              <column-options class="column-options" @click.native.stop="">
                <ul class="list mb-0">
                  <li
                    :id="`duplicateButton${data.item.pollId}`"
                    v-if="optionsControll.duplicate"
                    variant="link"
                    @click="duplicateCustomPoll(data.item.pollId)"
                  >
                    {{ buttons.duplicate }}
                  </li>
                  <li
                    :id="`archiveButton${data.item.pollId}`"
                    v-if="optionsControll.toFile"
                    variant="link"
                    @click="closeCustomPoll(data.item.pollId)"
                  >
                    {{ buttons.toFile }}
                  </li>
                  <li
                    :id="`deleteButton${data.item.pollId}`"
                    v-if="optionsControll.delete"
                    variant="link"
                    @click="closeDraft(data.item.pollId)"
                  >
                    {{ buttons.delete }}
                  </li>
                  <li
                    :id="`openLink${data.item.pollId}`"
                    v-if="optionsControll.answer"
                    variant="link"
                    @click="openPoll(data.item.pollId)"
                  >
                    {{ buttons.answer }}
                  </li>
                </ul>
              </column-options>
            </template>
          </b-table>
        </b-card>
      </section>
    </div>
  </b-card>
</template>

<script>
import Loading from '@/components/Shared/Loading.vue'
import { mapGetters } from 'vuex'
import {
  STATUS_ACTIVE,
  OWNER_MY,
  OWNER_OTHERS,
  STATUS_CLOSED,
  STATUS_ON_GOING,
  STATUS_DONE,
  STATUS_DRAFTS,
} from '@/components/CustomPoll/Enum.js'
import { cond, always, T, pipe, allPass, equals, flip, prop } from 'ramda'
import ColumnOptions from '@/components/CustomPoll/ColumnOptions.vue'

const props = {
  polls: {
    type: Array,
    default: () => [],
  },
  currentTab: {
    type: String,
    required: true,
  },
}

export default {
  props,
  components: {
    Loading,
    ColumnOptions,
  },
  data() {
    return {
      loadingPage: false,
      toDraft: false,
      selectedOwner: OWNER_MY,
      tableItemTogglePopover: false,
      mainToggle: false,
      status: {
        active: STATUS_ACTIVE,
        closed: STATUS_CLOSED,
        onGoing: STATUS_ON_GOING,
        done: STATUS_DONE,
        drafts: STATUS_DRAFTS,
      },
      owner: {
        my: OWNER_MY,
        others: OWNER_OTHERS,
      },
    }
  },
  watch: {
    selectedOwner(value) {
      const status =
        value === OWNER_OTHERS
          ? this.status.active
          : this.toDraft
          ? this.status.drafts
          : this.status.active

      if (this.toDraft) this.toDraft = false

      this.$emit('changeOwner', value)
      this.$emit('changeStatus', status)
    },
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
      loading: 'customPoll/loading',
    }),
    userRole() {
      return this.$store.getters['user/user'].role
    },
    user() {
      const profile = this.$store.getters['user/user'].profile
      return `${profile.firstname} ${profile.lastname}`
    },
    tdClass() {
      return this.currentTab !== this.status.done ? 'clickable' : ''
    },
    tableFields() {
      const labels = this.label.tableFields[this.currentTab]

      const buildFieldsObject = key => ({
        key,
        label: labels[key],
      })

      return [
        ...Object.keys(labels).map(buildFieldsObject),
        {
          settings: '',
        },
      ]
    },

    items() {
      const progressColor = progress => {
        switch (true) {
          case progress > 80:
            return 'outline-lima'
          case progress > 50:
            return 'outline-orange'
          default:
            return 'outline-red'
        }
      }
      const transformPollItem = item => ({
        pollId: item['poll_id'],
        // pollReportId: item['poll_report_id'],
        title: item['title'],
        finished: `${item['progress']}%`,
        addressee: item['num_employees'],
        activatedAt: this.$moment(item['created_date']).format('DD/MM/YYYY'),
        createdAt: this.$moment(item['created_date']).format('DD/MM/YYYY'),
        closedAt: this.$moment(item['closed_date']).format('DD/MM/YYYY'),
        createdBy: item['created_by'] || this.user,
        // settings: item,
        _cellVariants: { finished: progressColor(item['progress']) },
      })

      return this.polls.map(transformPollItem)
    },

    emptyLabel() {
      const {
        answerCustomPoll,
        completedCustomPoll,
        createCustomPoll,
      } = this.resources.page.customPoll.emptyState

      const isOnGoing = ([currentStatus, { onGoing }]) =>
        currentStatus === onGoing
      const isDone = ([currentStatus, { done }]) => currentStatus === done

      return cond([
        [isOnGoing, always(answerCustomPoll)],
        [isDone, always(completedCustomPoll)],
        [T, always(createCustomPoll)],
      ])([this.currentStatus, this.status])
    },

    label() {
      return this.resources.page.customPoll.dashboard
    },

    availableTabs() {
      const tabKey = !this.$can('see', 'customPoll.report')
        ? 'default'
        : this.selectedOwner

      const tabs = {
        [OWNER_MY]: 'status',
        [OWNER_OTHERS]: 'otherStatus',
        default: 'userStatus',
      }

      const _prop = flip(prop)
      const getTabs = _prop(tabs)
      const getLabel = _prop(this.label)

      const availableTabs = pipe(
        getTabs,
        getLabel
      )(tabKey)

      delete availableTabs.onGoing

      return availableTabs
    },
    buttons() {
      return {
        ...this.resources.general.buttons,
        ...this.label.tableFields.buttons,
      }
    },
    optionsControll() {
      const isManagerOtherPolls =
        this.userRole === 'manager' && this.selectedOwner === this.owner.others

      const options = {
        toFile: this.currentTab === this.status.active,
        delete:
          this.currentTab === this.status.drafts && this.userRole !== 'user',
        answer: this.currentTab === this.status.onGoing,
        duplicate:
          (this.currentTab === this.status.active ||
            this.currentTab === this.status.closed) &&
          this.$can('create', 'customPoll'),
      }

      const anyAvailableOption = Object.values(options).some(a => a)

      return {
        ...options,
        canSee: allPass([
          equals(!isManagerOtherPolls),
          equals(anyAvailableOption),
        ])(T()),
      }
    },
  },
  methods: {
    async openPoll(pollId) {
      this.loadingPage = true
      this.$router.push({ path: `/custom-poll/survey/${pollId}` })
    },
    openCustomPollRoute(pollId) {
      const routePath =
        this.currentTab === this.status.drafts ? 'edit' : 'report'
      return this.$router.push({
        name: `customPoll.${routePath}`,
        params: { pollId },
      })
    },
    rowClick({ pollId }) {
      if (this.currentTab === this.status.done) return false

      this.$can('see', 'customPoll.report') &&
      this.currentTab !== this.status.onGoing
        ? this.openCustomPollRoute(pollId)
        : this.openPoll(pollId)
    },
    closeCustomPoll(id) {
      this.$store.dispatch('customPoll/closeCustomPoll', id)
    },
    closeDraft(id) {
      this.$store.dispatch('customPoll/closeDraft', id)
    },
    duplicateCustomPoll(id) {
      this.$store.dispatch('customPoll/duplicateCustomPoll', id).then(() => {
        if (this.selectedOwner === OWNER_MY) {
          return this.$emit('changeStatus', STATUS_DRAFTS)
        }
        this.toDraft = true
        this.selectedOwner = OWNER_MY
      })
    },
  },
  beforeDestroy() {
    this.loadingPage = false
  },
}
</script>
<style scoped lang="scss">
.card {
  border-radius: 8px;

  .card-body {
    @media (max-width: 425px) {
      padding: 0.25rem;
    }
  }
}
.rounded-close {
  margin: 0 3px;
}
</style>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';

::v-deep .popover-absolute {
  top: 35px;
  right: 15px;

  .tc-popover {
    padding: 0;

    .tc-popover-body {
      margin: 0.25rem 0;
    }

    li {
      cursor: pointer;
      color: map-get($theme-colors, 'gray03');
      font-weight: 700;
      text-align: right;

      &:hover {
        background-color: map-get($theme-colors, 'gray08');
        color: map-get($theme-colors, 'primary');
      }
    }
  }
}

::v-deep .loading {
  justify-content: center;
  display: flex;
}

.card .card-body {
  .empty-labels {
    text-align: center;
    padding: 3rem 0;
    max-width: 800px;
    margin: 0 auto;

    @media (max-width: 425px) {
      text-align: center;
    }

    p {
      font-size: 38px;
      max-width: 80%;
      margin: 0 auto;

      @media (max-width: 768px) {
        max-width: initial;
      }

      @media (max-width: 425px) {
        font-size: 18px;
      }
    }

    small {
      font-size: 22px;
      max-width: 80%;
      margin: 10px auto;
      display: inline-block;

      @media (max-width: 425px) {
        font-size: 12px;
        max-width: initial;
      }
    }
  }

  .custom-search {
    header {
      text-align: center;

      .btn-group {
        .btn {
          background: map-get($theme-colors, 'gray06');
          border: 1px solid map-get($theme-colors, 'gray-dark');
          border-radius: initial !important;
          color: map-get($theme-colors, 'gray-dark');
          font-weight: 500;

          &.active {
            background: white;
            border: 1px solid
              transparentize(map-get($theme-colors, 'purple01'), 0.5);
            color: map-get($theme-colors, 'primary');
            box-shadow: 0 2px 4px
              transparentize(map-get($theme-colors, 'purple02'), 0.75);
          }

          @media (max-width: 425px) {
            font-size: small;
          }

          &:first-child {
            border-radius: 35px 0 0 35px !important;
          }

          &:last-child {
            border-radius: 0 35px 35px 0 !important;
          }
        }
      }
    }

    section {
      .card-body {
        padding: 0;

        ::v-deep .table {
          width: 100%;

          .column-options {
            display: flex;
            margin-left: auto;
            width: 2.65em;

            ul {
              list-style: none;
              padding: 0;
            }

            li {
              min-width: 250px;
              padding: 0.7rem;
              width: 33%;
            }
          }

          thead {
            border-top: unset !important;
          }

          thead th {
            border-left: none !important;
            font-size: 14px;
            font-weight: 500;
            padding-left: 10px;
          }

          thead,
          tbody tr {
            display: table;
            width: 100%;
            table-layout: fixed;

            &:last-child {
              margin-bottom: 90px !important;
            }
          }

          tbody {
            display: block;
            // max-height: 40vh;
            // min-height: 40vh;
            // overflow: auto;
            // padding-bottom: 80px;

            tr {
              min-height: 68px;
              &:hover {
                background: map-get($theme-colors, 'light') !important;
                box-shadow: transparentize(
                  map-get($theme-colors, 'purple02'),
                  0.76
                );
              }

              td {
                border-left: none !important;
                background: none !important;
                color: map-get($theme-colors, 'gray04');
                font-size: 18px;
                padding: 10px;
                vertical-align: middle;

                &.table-outline-lima {
                  color: map-get($theme-colors, 'lima');
                  font-weight: 500;
                }

                &.table-outline-orange {
                  color: map-get($theme-colors, 'orange');
                  font-weight: 500;
                }

                &.table-outline-red {
                  color: map-get($theme-colors, 'red');
                  font-weight: 500;
                }

                &:first-child {
                  font-weight: 500;
                  font-size: 18px;
                }
              }
            }
          }
        }
      }
    }
  }
}

.poll-loading {
  margin: 0 auto;
  text-align: center;
}
</style>
